import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import api, { errMsg } from "@/lib/api";
import { useStudent } from "@/context/StudentContext";
import { inputCls, btnPrimary, btnLink } from "@/components/portal/PortalUI";
import { GraduationCap, KeyRound, Mail, RefreshCw, AlertCircle } from "lucide-react";

export const StudentAuthShell = ({ title, subtitle, children }) => (
  <div className="min-h-screen flex flex-col bg-soft-gray">
    <Header />
    <main className="flex-1 flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md bg-white rounded-xl border border-navy/10 shadow-lg overflow-hidden">
        <div className="bg-navy px-8 py-7 text-white relative">
          <div className="absolute right-0 top-0 h-full w-2 bg-magenta" />
          <GraduationCap className="text-magenta mb-3" size={32} />
          <h1 className="font-display text-2xl font-black" data-testid="student-auth-title">{title}</h1>
          {subtitle && <p className="text-white/70 text-sm mt-1">{subtitle}</p>}
        </div>
        <div className="p-8">{children}</div>
      </div>
    </main>
    <Footer />
  </div>
);

const MODES = {
  login: { title: "Student Login", sub: "For approved and enrolled Driven Together students." },
  forgot: { title: "Reset Your Password", sub: "We'll email you a secure reset link." },
  magic: { title: "One-Time Login Link", sub: "We'll email you a secure link that signs you in once." },
  resend: { title: "Resend Registration Link", sub: "Haven't activated yet? Get a fresh invitation link." },
};

export default function StudentLogin() {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);
  const { setStudent } = useStudent();
  const navigate = useNavigate();

  const go = (m) => { setMode(m); setError(null); setInfo(""); };

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError(null); setInfo("");
    try {
      if (mode === "login") {
        const r = await api.post("/student/auth/login", { email, password });
        setStudent(r.data); navigate("/student");
      } else {
        const path = { forgot: "forgot-password", magic: "magic-link", resend: "resend-registration" }[mode];
        const r = await api.post(`/student/auth/${path}`, { email });
        setInfo(r.data.message);
      }
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError({ text: errMsg(err), code: d?.code });
    } finally { setBusy(false); }
  };

  return (
    <StudentAuthShell title={MODES[mode].title} subtitle={MODES[mode].sub}>
      <form onSubmit={submit} className="space-y-4" data-testid={`student-${mode}-form`}>
        <div>
          <label className="text-xs font-semibold text-navy">Email address used on your application</label>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={`${inputCls} mt-1`} data-testid="student-email-input" />
        </div>
        {mode === "login" && (
          <div>
            <label className="text-xs font-semibold text-navy">Password</label>
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={`${inputCls} mt-1`} data-testid="student-password-input" />
          </div>
        )}
        {error && (
          <div className="flex gap-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-md p-3" data-testid="student-auth-error">
            <AlertCircle size={18} className="shrink-0 mt-0.5" />
            <div>
              <p>{error.text}</p>
              {error.code === "registration_incomplete" && (
                <button type="button" onClick={() => go("resend")} className="mt-2 font-bold underline" data-testid="resend-registration-link">Resend Registration Link</button>
              )}
            </div>
          </div>
        )}
        {info && <p className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm rounded-md p-3" data-testid="student-auth-info">{info}</p>}
        <button disabled={busy} className={`${btnPrimary} w-full`} data-testid="student-auth-submit">
          {busy ? "Please wait…" : mode === "login" ? "Sign In" : "Send Link"}
        </button>
      </form>
      <div className="mt-6 pt-5 border-t border-navy/10 grid gap-2">
        {mode !== "login" && <button onClick={() => go("login")} className={btnLink} data-testid="mode-login"><KeyRound size={14} /> Back to sign in</button>}
        {mode !== "forgot" && <button onClick={() => go("forgot")} className={btnLink} data-testid="mode-forgot"><KeyRound size={14} /> Forgot password?</button>}
        {mode !== "magic" && <button onClick={() => go("magic")} className={btnLink} data-testid="mode-magic"><Mail size={14} /> Email me a one-time login link</button>}
        {mode !== "resend" && <button onClick={() => go("resend")} className={btnLink} data-testid="mode-resend"><RefreshCw size={14} /> Continue an incomplete registration</button>}
      </div>
      <p className="text-xs text-dark-text/50 mt-6">Student Portal access is created only after AAWTA approves and enrolls you. Not a student yet? <Link to="/intake" className="text-magenta font-semibold">Start your intake</Link>.</p>
    </StudentAuthShell>
  );
}
